import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { reviewApi } from '../utils/reviewApi';
import { Avatar, ago } from '../pages/review/reviewUi';
import '../styles/DocReview.css';

/**
 * The header bell (mockup screen 8) — Doc Review notifications.
 *
 * The COUNT comes from the 5-second collab poll (`counts.unread`), so the bell adds no timer
 * of its own. The LIST is read only when the bell is opened. Unread is server state
 * (`read_at IS NULL`, the same rule as chat — Critical Note #72): opening the list does not
 * mark anything read; clicking an item or "Mark all read" does.
 */
export default function ReviewBell({ counts }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const ref = useRef(null);
  const unread = counts?.unread || 0;

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    reviewApi.notifications()
      .then((d) => { if (live) { setItems(d.items || []); setError(''); } })
      .catch((e) => { if (live) setError(e.message); });
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { live = false; document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open, unread]);

  if (!counts) return null;            // module not licensed

  const markAll = async () => {
    try {
      await reviewApi.markRead([]);
      setItems((xs) => (xs || []).map((n) => ({ ...n, read_at: n.read_at || 'now' })));
    } catch (e) {
      setError(e.message);
    }
  };

  const clickItem = (n) => {
    if (!n.read_at) reviewApi.markRead([n.id]).catch(() => {});
    setOpen(false);
  };

  return (
    <div className="drv-bell" ref={ref}>
      <button
        type="button"
        className={`drv-bell-btn${open ? ' drv-bell-btn--open' : ''}`}
        aria-label={unread ? `Notifications, ${unread} new` : 'Notifications'}
        aria-expanded={open}
        title="Notifications"
        onClick={() => setOpen((o) => !o)}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
          strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.9 1.9 0 0 0 3.4 0" />
        </svg>
        {unread > 0 && <span className="drv-bell-count">{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && (
        <section className="drv-bell-menu" aria-label="Notifications">
          <div className="drv-bell-head">
            <h2>Notifications</h2>
            {items?.some((n) => !n.read_at) && (
              <button type="button" className="drv-link" style={{ fontSize: 12 }} onClick={markAll}>Mark all read</button>
            )}
          </div>
          <div className="drv-bell-list">
            {error && <div className="drv-error" style={{ margin: 12 }}>{error}</div>}
            {!items && !error && <div className="drv-bell-item drv-muted">Loading…</div>}
            {items?.length === 0 && <div className="drv-bell-item drv-muted">Nothing yet.</div>}
            {(items || []).map((n) => (
              <Link key={n.id} to={n.link || '/review'} onClick={() => clickItem(n)}
                className={`drv-bell-item${n.read_at ? '' : ' drv-bell-item--new'}`}>
                <span className={`drv-bell-dot${n.read_at ? ' drv-bell-dot--read' : ''}`} aria-hidden="true" />
                <Avatar name={n.actor_name || 'Doc Review'} />
                <span>
                  <strong>{n.title}</strong>
                  {n.body && <span className="drv-muted"> — {n.body}</span>}
                  <div className="drv-bell-when">{ago(n.created_at)}</div>
                </span>
              </Link>
            ))}
          </div>
          <div className="drv-bell-foot">
            <Link className="drv-link" style={{ fontSize: 12 }} to="/review" onClick={() => setOpen(false)}>Open Doc Review</Link>
          </div>
        </section>
      )}
    </div>
  );
}
